import { c as _c } from "react/compiler-runtime";
// Шаг «Материалы»: перетаскивание файлов и папок, вставка текста, файлы из
// заявки, список материалов и сетка фото. Файлы уходят по одному запросу на
// файл (не больше двух разом) — у каждого свой статус и «Повторить».
import { ChevronDown, ChevronRight, ClipboardPaste, Eye, FolderUp, Inbox, Loader2, RotateCcw, Trash2, Upload, WandSparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { CATALOG_IMPORT_FILE_MAX_BYTES, CATALOG_IMPORT_MAX_ITEMS, type CatalogImportFile, type CatalogImportJobDetail } from "@osmix/contracts";
import { useAddImportFromApplication, useAddImportText, useCatalogImportModels, useDeleteImportFile, useParseImport, useUploadImportFile } from "@/api/hooks/use-catalog-import";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { cn } from "@/lib/utils";
import { FILE_STATUS_LABELS, FileKindIcon, ImportThumb, ProgressBar, TemplateDownload, errorText, formatBytes } from "./shared";
type UploadRow = {
  key: string;
  file: File;
  state: "queued" | "uploading" | "done" | "error";
  error?: string;
  /** Сколько файлов получилось (архив распаковывается). */
  created?: number;
};
const PARALLEL = 2;
const IMAGES_PAGE = 40;
/** Служебные файлы систем — в материалы не берём. */
const JUNK = /^(\.ds_store|thumbs\.db|desktop\.ini|\._.*)$/i;

/**
 * Путь файла внутри перетащенной папки («Платья/Ривьера/1.jpg»). Шлюз
 * раскладывает фото по товарам и по папкам — без пути от папки остаются одни
 * имена файлов. У выбора папки через диалог путь есть в `webkitRelativePath`.
 */
const dropPaths = new WeakMap<File, string>();
const relativePathOf = (file: File): string | undefined => dropPaths.get(file) || file.webkitRelativePath || undefined;

/** Файлы из перетаскивания: папки обходятся рекурсивно. */
const filesFromDrop = async (dt: DataTransfer): Promise<File[]> => {
  // Записи надо взять синхронно, пока событие живо: после await список пуст.
  const entries = Array.from(dt.items).map(item => item.kind === "file" ? item.webkitGetAsEntry?.() : null).filter((entry): entry is FileSystemEntry => !!entry);
  if (entries.length === 0) return Array.from(dt.files);
  const out: File[] = [];
  const walk = async (entry: FileSystemEntry): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
      // Одиночный файл (не из папки) пути не несёт: fullPath = «/имя».
      const path = entry.fullPath.replace(/^\/+/, "");
      if (path.includes("/")) dropPaths.set(file, path);
      out.push(file);
      return;
    }
    if (!entry.isDirectory) return;
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
      if (batch.length === 0) break;
      for (const child of batch) await walk(child);
    }
  };
  for (const entry of entries) await walk(entry);
  return out;
};
export function MaterialsStep({
  job,
  canEdit,
  onOpenFile,
  onParsed
}: {
  job: CatalogImportJobDetail;
  canEdit: boolean;
  onOpenFile: (file: CatalogImportFile) => void;
  onParsed: () => void;
}) {
  const editable = canEdit && (job.status === "draft" || job.status === "review" || job.status === "failed");
  const uploadMutation = useUploadImportFile(job.id);
  const parse = useParseImport(job.id);
  const deleteFile = useDeleteImportFile(job.id);
  const models = useCatalogImportModels().data?.models ?? [];
  const model = models.find(m => m.id === job.model);
  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const started = useRef(new Set<string>());
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmReparse, setConfirmReparse] = useState(false);
  const [deleting, setDeleting] = useState<CatalogImportFile | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);

  // Выбор папки: атрибут нестандартный, в типах React его нет.
  useEffect(() => {
    folderInput.current?.setAttribute("webkitdirectory", "");
  }, []);
  const enqueue = (files: File[]) => {
    const rows = files.filter(file => !JUNK.test(file.name)).map<UploadRow>(file_0 => ({
      key: `${file_0.name}:${file_0.size}:${file_0.lastModified}:${Math.random().toString(36).slice(2, 8)}`,
      file: file_0,
      ...(file_0.size > CATALOG_IMPORT_FILE_MAX_BYTES ? {
        state: "error" as const,
        error: "Больше 100 МБ — разбейте архив на части"
      } : file_0.size === 0 ? {
        state: "error" as const,
        error: "Пустой файл"
      } : {
        state: "queued" as const
      })
    }));
    if (rows.length) setUploads(prev => [...prev, ...rows]);
  };

  // Очередь: не больше двух загрузок разом, следующая — как только освободилось место.
  useEffect(() => {
    const active = uploads.filter(u => u.state === "uploading").length;
    const next = uploads.filter(u_0 => u_0.state === "queued" && !started.current.has(u_0.key)).slice(0, PARALLEL - active);
    for (const row of next) {
      started.current.add(row.key);
      setUploads(prev_0 => prev_0.map(u_1 => u_1.key === row.key ? {
        ...u_1,
        state: "uploading"
      } : u_1));
      uploadMutation.mutateAsync({
        file: row.file,
        path: relativePathOf(row.file)
      }).then(files_0 => setUploads(prev_1 => prev_1.map(u_2 => u_2.key === row.key ? {
        ...u_2,
        state: "done",
        created: files_0.length
      } : u_2))).catch(e => setUploads(prev_2 => prev_2.map(u_3 => u_3.key === row.key ? {
        ...u_3,
        state: "error",
        error: errorText(e)
      } : u_3)));
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: очередь двигается только сменой строк
  }, [uploads]);
  const retry = (key: string) => {
    started.current.delete(key);
    setUploads(prev_3 => prev_3.map(u_4 => u_4.key === key ? {
      ...u_4,
      state: "queued",
      error: undefined
    } : u_4));
  };
  const busyUploads = uploads.some(u_5 => u_5.state === "queued" || u_5.state === "uploading");
  const doneCount = uploads.filter(u_6 => u_6.state === "done").length;
  const totalBytes = uploads.reduce((sum, u_7) => sum + u_7.file.size, 0);
  const doneBytes = uploads.filter(u_8 => u_8.state === "done").reduce((sum_0, u_9) => sum_0 + u_9.file.size, 0);
  const onDrop = async (e_0: React.DragEvent) => {
    e_0.preventDefault();
    setDragging(false);
    if (!editable) return;
    setReading(true);
    try {
      enqueue(await filesFromDrop(e_0.dataTransfer));
    } catch (err) {
      setError(`Не удалось прочитать перетащенные файлы: ${errorText(err)}`);
    } finally {
      setReading(false);
    }
  };
  const runParse = async () => {
    setError(null);
    try {
      await parse.mutateAsync();
      setConfirmReparse(false);
      onParsed();
    } catch (e_1) {
      setError(errorText(e_1));
      setConfirmReparse(false);
    }
  };
  const topLevel = job.files.filter(f => !f.parentId);
  const images = job.files.filter(f_0 => f_0.isImage);
  const hasItems = job.items.length > 0;
  return <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
			<div className="flex min-w-0 flex-col gap-4">
				{editable && <div onDragOver={e_2 => {
        e_2.preventDefault();
        setDragging(true);
      }} onDragLeave={() => setDragging(false)} onDrop={e_3 => void onDrop(e_3)} className={cn("flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-4 py-8 text-center transition-colors sm:py-10", dragging ? "border-primary bg-primary/5" : "bg-muted/30")}>
						<span className="grid size-11 place-items-center rounded-full border bg-background">
							{reading ? <Loader2 className="size-5 animate-spin" /> : <Upload className="size-5" />}
						</span>
						<div>
							<p className="font-medium">Перетащите файлы или папку сюда</p>
							<p className="text-sm text-muted-foreground">
								Прайс, каталог PDF, Word, фото, архив — до 100 МБ на файл
							</p>
						</div>
						<div className="flex flex-wrap justify-center gap-2">
							<Button variant="outline" size="sm" onClick={() => fileInput.current?.click()}>
								<Upload className="size-4" />
								Выбрать файлы
							</Button>
							<Button variant="outline" size="sm" onClick={() => folderInput.current?.click()}>
								<FolderUp className="size-4" />
								Выбрать папку
							</Button>
						</div>
						<input ref={fileInput} type="file" multiple className="hidden" onChange={e_4 => {
          enqueue(Array.from(e_4.target.files ?? []));
          e_4.target.value = "";
        }} />
						<input ref={folderInput} type="file" multiple className="hidden" onChange={e_5 => {
          enqueue(Array.from(e_5.target.files ?? []));
          e_5.target.value = "";
        }} />
					</div>}

				{uploads.length > 0 && <section className="rounded-lg border p-3 sm:p-4">
						<div className="mb-2 flex flex-wrap items-center justify-between gap-2">
							<h3 className="text-sm font-semibold">
								Загрузка файлов: {doneCount} из {uploads.length}
								<span className="ml-2 font-normal text-muted-foreground tabular-nums">
									{formatBytes(doneBytes)} из {formatBytes(totalBytes)}
								</span>
							</h3>
							{!busyUploads && <Button variant="ghost" size="sm" onClick={() => setUploads([])}>
									Скрыть
								</Button>}
						</div>
						<ProgressBar value={doneBytes} max={totalBytes} className="mb-3" />
						<ul className="max-h-64 divide-y overflow-y-auto">
							{uploads.map(u_10 => <li key={u_10.key} className="flex items-center gap-2 py-1.5 text-sm">
									{u_10.state === "uploading" ? <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" /> : u_10.state === "done" ? <span className="size-2 shrink-0 rounded-full bg-emerald-500" aria-hidden /> : u_10.state === "error" ? <span className="size-2 shrink-0 rounded-full bg-destructive" aria-hidden /> : <span className="size-2 shrink-0 rounded-full bg-muted-foreground/40" aria-hidden />}
									<span className="min-w-0 flex-1 truncate" title={u_10.file.name}>
										{u_10.file.name}
									</span>
									<span className="shrink-0 text-xs text-muted-foreground tabular-nums">{formatBytes(u_10.file.size)}</span>
									<span className={cn("hidden w-40 shrink-0 truncate text-right text-xs sm:block", u_10.state === "error" ? "text-destructive" : "text-muted-foreground")} title={u_10.error}>
										{u_10.state === "queued" ? "в очереди" : u_10.state === "uploading" ? "загружается…" : u_10.state === "done" ? u_10.created && u_10.created > 1 ? `готово, файлов: ${u_10.created}` : "готово" : u_10.error}
									</span>
									{u_10.state === "error" && u_10.file.size <= CATALOG_IMPORT_FILE_MAX_BYTES && u_10.file.size > 0 && <Button variant="ghost" size="icon-sm" title="Повторить" aria-label={`Повторить ${u_10.file.name}`} onClick={() => retry(u_10.key)}>
											<RotateCcw className="size-4" />
										</Button>}
									{u_10.state === "error" && <Button variant="ghost" size="icon-sm" title="Убрать из списка" aria-label={`Убрать ${u_10.file.name}`} onClick={() => setUploads(prev_4 => prev_4.filter(x => x.key !== u_10.key))}>
											<X className="size-4" />
										</Button>}
								</li>)}
						</ul>
						{uploads.some(u_13 => u_13.state === "error") && <p className="mt-2 text-xs text-destructive sm:hidden">
								Не загрузились: {uploads.filter(u_11 => u_11.state === "error").map(u_12 => u_12.file.name).join(", ")}
							</p>}
					</section>}

				<FilesList files={topLevel} all={job.files} editable={editable} onOpen={onOpenFile} onDelete={setDeleting} />

				{images.length > 0 && <ImagesGrid jobId={job.id} images={images} onOpen={onOpenFile} />}
			</div>

			<aside className="flex min-w-0 flex-col gap-4">
				<section className="rounded-lg border p-4 text-sm">
					<h3 className="mb-2 font-semibold">Что можно кидать</h3>
					<ul className="list-disc space-y-1 pl-4 text-muted-foreground">
						<li>Прайс или выгрузку из WB, Ozon, 1С — xlsx, csv</li>
						<li>Каталог или лукбук PDF, документ Word</li>
						<li>Фото — по одному, архивом или папкой: по папкам модель разложит фото по товарам</li>
						<li>Текст из переписки с продавцом</li>
					</ul>
					<p className="mt-3 text-muted-foreground">
						Быстрее и точнее всего — наш шаблон: его читаем без модели. Позиций в задании — до{" "}
						{CATALOG_IMPORT_MAX_ITEMS.toLocaleString("ru-RU")}.
					</p>
					<TemplateDownload variant="link" className="mt-2" />
				</section>

				{editable && <PasteText jobId={job.id} />}
				{editable && job.application && <FromApplication jobId={job.id} shopName={job.application.shopName} />}
			</aside>

			{canEdit && (job.status === "draft" || job.status === "review" || job.status === "failed") && <div className="sticky bottom-2 z-10 flex flex-col gap-3 rounded-lg border bg-background p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-4 lg:col-span-2">
					<div className="min-w-0 text-sm">
						<p className="font-medium">
							{job.files.length === 0 ? "Добавьте хотя бы один файл или текст" : `Материалов: ${job.files.length}, фото: ${images.length}`}
						</p>
						<p className="text-muted-foreground">
							Модель: {model?.label ?? job.model}
							{hasItems ? " · повторный разбор заменит черновики и правки" : ""}
						</p>
						{error && <p role="alert" className="mt-1 text-destructive">
								{error}
							</p>}
					</div>
					<Button className="shrink-0" disabled={job.files.length === 0 || busyUploads || parse.isPending} onClick={() => hasItems ? setConfirmReparse(true) : void runParse()} title={busyUploads ? "Дождитесь загрузки файлов" : undefined}>
						{parse.isPending ? <Loader2 className="size-4 animate-spin" /> : <WandSparkles className="size-4" />}
						{hasItems || job.status === "failed" ? "Разобрать заново" : "Разобрать"}
					</Button>
				</div>}

			<ConfirmDialog open={confirmReparse} busy={parse.isPending} title="Разобрать материалы заново?" description={`Сейчас в задании ${job.items.length} позиций. Повторный разбор заменит их новыми черновиками — ручные правки пропадут.`} confirmLabel="Разобрать заново" confirmVariant="destructive" onConfirm={() => void runParse()} onClose={() => setConfirmReparse(false)} />
			<ConfirmDialog open={!!deleting} busy={deleteFile.isPending} title={`Удалить «${deleting?.filename ?? ""}»?`} description={deleting?.kind === "archive" ? "Вместе с архивом уйдут и распакованные из него файлы; их фото пропадут у позиций." : deleting?.usedByItems ? `Файл используют позиции: ${deleting.usedByItems}. Фото пропадут у них.` : undefined} confirmLabel="Удалить" confirmVariant="destructive" onConfirm={() => {
      if (!deleting) return;
      deleteFile.mutate(deleting.id, {
        onSuccess: () => setDeleting(null),
        onError: e_6 => {
          setError(errorText(e_6));
          setDeleting(null);
        }
      });
    }} onClose={() => setDeleting(null)} />
		</div>;
}
function FilesList(t0) {
  const $ = _c(25);
  const {
    files,
    all,
    editable,
    onOpen,
    onDelete
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expanded, setExpanded] = useState(t1);
  let t2;
  if ($[1] !== all) {
    t2 = id => all.filter(f => f.parentId === id);
    $[1] = all;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const childrenOf = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = id_0 => setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id_0)) {
        next.delete(id_0);
      } else {
        next.add(id_0);
      }
      return next;
    });
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const toggle = t3;
  if (files.length === 0) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <section className="flex flex-col items-center gap-2 rounded-lg border p-8 text-center text-sm text-muted-foreground"><Inbox className="size-6" />Материалов пока нет.</section>;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  let t4;
  if ($[5] !== all.length) {
    t4 = <h3 className="border-b px-3 py-2 text-sm font-semibold sm:px-4">Материалы задания <span className="font-normal text-muted-foreground">· {all.length}</span></h3>;
    $[5] = all.length;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== childrenOf || $[8] !== editable || $[9] !== expanded || $[10] !== files || $[11] !== onDelete || $[12] !== onOpen) {
    let t6;
    if ($[14] !== childrenOf || $[15] !== editable || $[16] !== expanded || $[17] !== onDelete || $[18] !== onOpen) {
      t6 = file => {
        const kids = file.kind === "archive" ? childrenOf(file.id) : [];
        const open = expanded.has(file.id);
        return <li key={file.id}><FileRow file={file} editable={editable} onOpen={onOpen} onDelete={onDelete}>{kids.length > 0 && <button type="button" onClick={() => toggle(file.id)} className="inline-flex items-center gap-0.5 text-xs text-primary hover:underline">{open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}{open ? "\u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C" : `Внутри файлов: ${kids.length}`}</button>}</FileRow>{open && <ul className="max-h-80 divide-y overflow-y-auto border-t bg-muted/20 pl-6">{kids.map(kid => <li key={kid.id}><FileRow file={kid} editable={false} onOpen={onOpen} onDelete={onDelete} /></li>)}</ul>}</li>;
      };
      $[14] = childrenOf;
      $[15] = editable;
      $[16] = expanded;
      $[17] = onDelete;
      $[18] = onOpen;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    t5 = files.map(t6);
    $[7] = childrenOf;
    $[8] = editable;
    $[9] = expanded;
    $[10] = files;
    $[11] = onDelete;
    $[12] = onOpen;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[20] !== t5) {
    t6 = <ul className="divide-y">{t5}</ul>;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t4 || $[23] !== t6) {
    t7 = <section className="rounded-lg border">{t4}{t6}</section>;
    $[22] = t4;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function FileRow(t0) {
  const $ = _c(50);
  const {
    file,
    editable,
    onOpen,
    onDelete,
    children
  } = t0;
  let t1;
  if ($[0] !== file.kind) {
    t1 = <FileKindIcon kind={file.kind} className="mt-0.5" />;
    $[0] = file.kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== file || $[3] !== onOpen) {
    t2 = () => onOpen(file);
    $[2] = file;
    $[3] = onOpen;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = file.path || file.filename;
  const t4 = file.path || file.filename;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = <button type="button" onClick={t2} className="block max-w-full truncate text-left text-sm font-medium hover:underline" title={t3}>{t4}</button>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== file.sizeBytes) {
    t6 = formatBytes(file.sizeBytes);
    $[9] = file.sizeBytes;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <span className="tabular-nums">{t6}</span>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = file.status === "failed" && "text-destructive";
  const t9 = file.status === "skipped" && "text-amber-700 dark:text-amber-500";
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = cn(t8, t9);
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = FILE_STATUS_LABELS[file.status];
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = <span className={t10}>{t11}</span>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== file.usedByItems) {
    t13 = file.usedByItems > 0 && <span>в позициях: {file.usedByItems}</span>;
    $[19] = file.usedByItems;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== file.note) {
    t14 = file.note && <span className="min-w-0">· {file.note}</span>;
    $[21] = file.note;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t12 || $[24] !== t13 || $[25] !== t14 || $[26] !== t7) {
    t15 = <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">{t7}{t12}{t13}{t14}</div>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t7;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== children || $[29] !== t15 || $[30] !== t5) {
    t16 = <div className="min-w-0 flex-1">{t5}{t15}{children}</div>;
    $[28] = children;
    $[29] = t15;
    $[30] = t5;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const t17 = `Открыть ${file.filename}`;
  let t18;
  if ($[32] !== file || $[33] !== onOpen) {
    t18 = () => onOpen(file);
    $[32] = file;
    $[33] = onOpen;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Eye className="size-4" />;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t17 || $[37] !== t18) {
    t20 = <Button variant="ghost" size="icon-sm" aria-label={t17} title={"\u041E\u0442\u043A\u0440\u044B\u0442\u044C"} onClick={t18}>{t19}</Button>;
    $[36] = t17;
    $[37] = t18;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== editable || $[40] !== file || $[41] !== onDelete) {
    t21 = editable && <Button variant="ghost" size="icon-sm" aria-label={`Удалить ${file.filename}`} title={"\u0423\u0434\u0430\u043B\u0438\u0442\u044C"} onClick={() => onDelete(file)}><Trash2 className="size-4" /></Button>;
    $[39] = editable;
    $[40] = file;
    $[41] = onDelete;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t20 || $[44] !== t21) {
    t22 = <div className="flex shrink-0 items-center">{t20}{t21}</div>;
    $[43] = t20;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t1 || $[47] !== t16 || $[48] !== t22) {
    t23 = <div className="flex items-start gap-3 px-3 py-2 sm:px-4">{t1}{t16}{t22}</div>;
    $[46] = t1;
    $[47] = t16;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  return t23;
}
function ImagesGrid(t0) {
  const $ = _c(19);
  const {
    jobId,
    images,
    onOpen
  } = t0;
  const [limit, setLimit] = useState(IMAGES_PAGE);
  let t1;
  if ($[0] !== images.length) {
    t1 = <h3 className="mb-3 text-sm font-semibold">Фото <span className="font-normal text-muted-foreground">· {images.length}</span></h3>;
    $[0] = images.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== images || $[3] !== jobId || $[4] !== limit || $[5] !== onOpen) {
    let t3;
    if ($[7] !== jobId || $[8] !== onOpen) {
      t3 = file => <button key={file.id} type="button" onClick={() => onOpen(file)} title={file.path || file.filename} className={cn("flex min-w-0 flex-col gap-1 rounded-md p-1 text-left transition-colors hover:bg-muted/60", file.status === "failed" && "opacity-60")}><ImportThumb jobId={jobId} fileId={file.status === "failed" ? null : file.id} className="aspect-[4/5] size-auto w-full" /><span className="truncate text-[11px] text-muted-foreground">{file.path ? file.path.split("/").slice(-2).join("/") : file.filename}</span></button>;
      $[7] = jobId;
      $[8] = onOpen;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t2 = images.slice(0, limit).map(t3);
    $[2] = images;
    $[3] = jobId;
    $[4] = limit;
    $[5] = onOpen;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 xl:grid-cols-8">{t2}</div>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== images.length || $[13] !== limit) {
    t4 = images.length > limit && <div className="mt-3 flex justify-center"><Button variant="outline" size="sm" onClick={() => setLimit(images.length)}>Показать все {images.length}</Button></div>;
    $[12] = images.length;
    $[13] = limit;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t1 || $[16] !== t3 || $[17] !== t4) {
    t5 = <section className="rounded-lg border p-3 sm:p-4">{t1}{t3}{t4}</section>;
    $[15] = t1;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
function PasteText({
  jobId
}: {
  jobId: string;
}) {
  const addText = useAddImportText(jobId);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [result, setResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const submit = async () => {
    setResult(null);
    try {
      const file = await addText.mutateAsync({
        text: text.trim(),
        filename: name.trim() || undefined
      });
      setText("");
      setName("");
      setResult({
        ok: true,
        message: `Добавлено: «${file.filename}»`
      });
    } catch (e) {
      setResult({
        ok: false,
        message: errorText(e)
      });
    }
  };
  return <section className="flex flex-col gap-2 rounded-lg border p-4">
			<h3 className="flex items-center gap-2 text-sm font-semibold">
				<ClipboardPaste className="size-4" />
				Вставить текст
			</h3>
			<p className="text-xs text-muted-foreground">Переписка, описание коллекции, список позиций с ценами.</p>
			<Input value={name} onChange={e_0 => setName(e_0.target.value)} placeholder="Название, например «Переписка в WhatsApp»" aria-label="Название текста" className="h-9" />
			<textarea value={text} onChange={e_1 => setText(e_1.target.value)} placeholder="Вставьте текст сюда" aria-label="Текст" className="min-h-28 w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 md:text-sm" />
			<Button variant="outline" disabled={!text.trim() || addText.isPending} onClick={() => void submit()}>
				{addText.isPending && <Loader2 className="size-4 animate-spin" />}
				Добавить текст
			</Button>
			{result && <p className={cn("text-xs", result.ok ? "text-muted-foreground" : "text-destructive")}>{result.message}</p>}
		</section>;
}
function FromApplication({
  jobId,
  shopName
}: {
  jobId: string;
  shopName: string;
}) {
  const add = useAddImportFromApplication(jobId);
  const [result, setResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const run = async () => {
    setResult(null);
    try {
      const files = await add.mutateAsync(undefined);
      setResult({
        ok: true,
        message: files.length ? `Взяли из заявки файлов: ${files.length}` : "В заявке нет файлов"
      });
    } catch (e) {
      setResult({
        ok: false,
        message: errorText(e)
      });
    }
  };
  return <section className="flex flex-col gap-2 rounded-lg border p-4">
			<h3 className="text-sm font-semibold">Файлы из заявки</h3>
			<p className="text-xs text-muted-foreground">
				Прайсы и фото, которые продавец «{shopName}» прислал в заявку, — без повторной загрузки.
			</p>
			<Button variant="outline" disabled={add.isPending} onClick={() => void run()}>
				{add.isPending && <Loader2 className="size-4 animate-spin" />}
				Взять файлы из заявки
			</Button>
			{result && <p className={cn("text-xs", result.ok ? "text-muted-foreground" : "text-destructive")}>{result.message}</p>}
		</section>;
}